import { Brand } from './brand.model';

/**
 * The brand registry: the one place every brand's identity, theme and host rule
 * live. Changing a name, logo, colour or backdrop is an edit here and nowhere
 * else.
 *
 * The new brand ("Perform") is the default everywhere. The legacy brand
 * ("Frankli") is confined to its own production hosts: every frankli.io host uses
 * the legacy brand EXCEPT the exception subdomains (the testing/preview
 * instances), which run the new default. Resolution is by host (see
 * resolveBrandForHost), so both run in parallel from the same deployment; the
 * transition finishes by deleting the legacy brand and its rule.
 */

/**
 * The legacy brand. Its assets keep the historical `frankli-*` / `Frankli_*`
 * filenames for now; once the switch is finalised they move behind a `legacy-`
 * prefix and are deleted with the brand.
 */
export const FRANKLI_BRAND: Brand = {
  key: 'frankli',
  name: 'Frankli',
  logos: {
    iconOnly: 'assets/img/logos/frankli-logo.svg',
    verticalDark: 'assets/img/logos/frankli-logo-text-dark.svg',
    textDark: 'assets/img/logos/frankli-logo-text-dark.svg',
    textWhite: 'assets/img/logos/frankli-logo-text-white.svg',
    primary: 'assets/img/Frankli_logo_primary.png',
    primaryWhite: 'assets/img/Frankli_logo_primary_white.png'
  },
  favicon: 'assets/img/favicon.png',
  theme: {
    // Today's look — mirrors the fallbacks in button.component.scss.
    buttonBg: '#30747F',
    buttonBgHover: '#662400',
    // Frankli keeps its own login backdrop — the circles artwork with no colour
    // behind it (a cover image), exactly as it renders on develop.
    loginBackgroundImage: 'url("/assets/img/background/circles2.png")',
    loginBackgroundColor: 'transparent'
  },
  domain: {
    help: 'https://help.frankli.io',
    web: 'https://www.frankli.io',
    app: 'https://app.frankli.io'
  }
};

/**
 * The new default brand. The stacked `perform-logo-vertical.svg` fronts the login
 * card, `perform-logo.svg` is the wordmark, `perform-logo-horizontal-white.svg`
 * the dark-background wordmark, `perform-logo-white.svg` the server-unreachable
 * overlay and `perform-logo-icon.svg` the compact/favicon slots. The CTA fill is
 * a distinct colour so the resolver is visibly verifiable; the domain still
 * points at Frankli's until the brand's own domain is decided.
 */
export const PERFORM_BRAND: Brand = {
  key: 'perform',
  name: 'Perform',
  logos: {
    iconOnly: 'assets/img/perform-logo-icon.svg',
    verticalDark: 'assets/img/perform-logo-vertical.svg',
    textDark: 'assets/img/perform-logo.svg',
    textWhite: 'assets/img/perform-logo-horizontal-white.svg',
    primary: 'assets/img/perform-logo.svg',
    primaryWhite: 'assets/img/perform-logo-white.svg'
  },
  favicon: 'assets/img/perform-logo-icon.svg',
  theme: {
    buttonBg: '#ff6c1c',
    buttonBgHover: '#662400',
    loginBackgroundImage: 'url("/assets/img/background/public-background.svg")',
    loginBackgroundColor: '#F1EAE4'
  },
  domain: { ...FRANKLI_BRAND.domain }
};

/** Every registered brand, keyed by Brand.key. */
export const BRANDS: Record<string, Brand> = {
  [FRANKLI_BRAND.key]: FRANKLI_BRAND,
  [PERFORM_BRAND.key]: PERFORM_BRAND
};

/** The brand served for any host not pinned to the legacy brand. */
export const DEFAULT_BRAND_KEY = PERFORM_BRAND.key;

/** The domain whose subdomains carry the legacy brand. */
export const LEGACY_BRAND_BASE_DOMAIN = 'frankli.io';

/**
 * Subdomains of the base domain that opt OUT of the legacy brand and run the new
 * default instead — the testing/preview instances. `qa3` is the parallel-run
 * check host; `dev` is the develop deployment. Everything else on frankli.io
 * stays legacy, including any future subdomain, until this list or the brand is
 * removed.
 */
export const LEGACY_BRAND_EXCEPTION_SUBDOMAINS = ['qa3', 'dev'];

/** Whether a host should be served the legacy (Frankli) brand. */
function isLegacyHost(host: string): boolean {
  const base = LEGACY_BRAND_BASE_DOMAIN;
  // Off the base domain (localhost, other domains) → new default brand.
  if (host !== base && !host.endsWith(`.${base}`)) {
    return false;
  }
  // The subdomain is everything before ".<base>" (empty for the apex).
  const subdomain = host === base ? '' : host.slice(0, -(base.length + 1));
  return !LEGACY_BRAND_EXCEPTION_SUBDOMAINS.includes(subdomain);
}

/**
 * Resolves the active brand for a host. Pure and synchronous so it can be shared
 * by the in-app service and the pre-bootstrap chrome without them diverging.
 *
 * Resolution order:
 *   1. an explicit override key (dev-only, supplied by the caller) — used to
 *      demonstrate parallel-run locally without DNS;
 *   2. the host rule — a frankli.io host that is not an exception → legacy brand;
 *   3. the default brand (the new Perform brand) for everything else.
 */
export function resolveBrandForHost(host: string, overrideKey?: string | null): Brand {
  if (overrideKey && BRANDS[overrideKey]) {
    return BRANDS[overrideKey];
  }

  const normalisedHost = (host || '').toLowerCase().split(':')[0];
  if (isLegacyHost(normalisedHost)) {
    return FRANKLI_BRAND;
  }
  return BRANDS[DEFAULT_BRAND_KEY] ?? PERFORM_BRAND;
}
